import React from "react";
import {
	addTimeToDate,
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	dateTrunc,
	Else,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	Row,
	Section,
	sort,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import { InternalLink } from "@hex-insights/router";
import { CalendarEventUtilities } from "../../Models";
import {
	announcementModelColor,
	AnnouncementModelIcon,
	calendarEventModelColor,
	CalendarEventModelIcon,
	CommunicationCreateModalController,
	CommunicationCreatePermissionRequired,
	newsletterModelColor,
	NewsletterModelIcon,
	postModelColor,
	PostModelIcon,
} from "../../Models/Components/_General";
import {
	AnnouncementAdminCard,
	AnnouncementCreateModalController,
	AnnouncementCreatePermissionRequired,
	ControlledAnnouncementsListModal,
} from "../../Models/Components/Announcement";
import {
	CalendarEventAdminCard,
	CalendarEventCreateModalController,
	CalendarEventCreatePermissionRequired,
	ViewerBigCalendar,
} from "../../Models/Components/CalendarEvent";
import {
	ControlledNewslettersListModal,
	NewsletterAdminCard,
	NewsletterCreateModalController,
	NewsletterCreatePermissionRequired,
} from "../../Models/Components/Newsletter";
import { PostAdminCard, PostCreateModalController, PostCreatePermissionRequired } from "../../Models/Components/Post";
import {
	AnnouncementFilterInput,
	AnnouncementOrderField,
	AnnouncementStatistics,
	CalendarEventFilterInput,
	CalendarEventOrderField,
	CalendarEventStatistics,
	defaultAnnouncementStatistics,
	defaultCalendarEventStatistics,
	defaultNewsletterStatistics,
	defaultPostStatistics,
	mergeFilterInputs,
	NewsletterFilterInput,
	NewsletterOrderField,
	NewsletterStatistics,
	OrderDirection,
	permissions,
	PostFilterInput,
	PostOrderField,
	PostStatistics,
	TimelineFilterInput,
	TimelineObjectType,
	TimelineViewerType,
	useAnnouncementCommunicationCenterPageListQuery,
	useCalendarEventCalendarDetailedListQuery,
	useCommunicationCenterStatisticsQuery,
	useNewsletterDisplayListQuery,
	useTimelinePostListQuery,
} from "../../Utilities";
import { EmptyView } from "../Empty";
import { Icon } from "../Icon";
import { ButtonNav, useButtonNav } from "../Nav";
import { statisticColors, StatisticWithBar } from "../Statistics";
import { Tile } from "../Tile";
import { Timeline } from "../Timeline";
import styles from "./styles.module.css";

const allViewPermissions = [
	permissions.Announcement.View.Admin,
	permissions.Newsletter.View.Admin,
	permissions.Post.View.Admin,
	permissions.CalendarEvent.View.Admin,
];

export type CommunicationCenterPageContentProps = {
	announcementIndexPageTo?: string;
	newsletterIndexPageTo?: string;
	postIndexPageTo?: string;
	calendarEventIndexPageTo?: string;
};

export function CommunicationCenterPageContent({
	announcementIndexPageTo,
	newsletterIndexPageTo,
	postIndexPageTo,
	calendarEventIndexPageTo,
}: CommunicationCenterPageContentProps) {
	const nav = useButtonNav(
		"communicationCenterPage",
		() => [
			{ value: "all", label: "All" },
			{ value: "personal", label: "Personal" },
		],
		{ defaultValue: "personal" },
	);

	return (
		<React.Fragment>
			<Row justify="space-between" style={{ margin: "var(--heading--level-1---margin)" }}>
				<Row justify="spaced-start" align="flex-end" horizontalSpacing="1.5rem">
					<Heading.H1 noMargin>Communication Centre</Heading.H1>

					<PermissionRequired requiredPermissions={allViewPermissions} checkMethod="or">
						<ButtonNav {...nav} />
					</PermissionRequired>
				</Row>

				<CommunicationCreatePermissionRequired>
					<CommunicationCreateModalController variant="primary" />
				</CommunicationCreatePermissionRequired>
			</Row>

			<CommunicationCenterPageBody
				key={nav.value}
				view={nav.value}
				announcementIndexPageTo={announcementIndexPageTo}
				newsletterIndexPageTo={newsletterIndexPageTo}
				postIndexPageTo={postIndexPageTo}
				calendarEventIndexPageTo={calendarEventIndexPageTo}
			/>
		</React.Fragment>
	);
}

const announcementCreatePermissions = [permissions.Announcement.Create.Admin, permissions.Announcement.Create.Basic];
const newsletterCreatePermissions = [permissions.Newsletter.Create.Admin, permissions.Newsletter.Create.Basic];
const postCreatePermissions = [permissions.Post.Create.Admin, permissions.Post.Create.Basic];
const calendarEventCreatePermissions = [permissions.CalendarEvent.Create.Admin, permissions.CalendarEvent.Create.Basic];

type CommunicationCenterPageBodyProps = {
	view: "personal" | "all";
	announcementIndexPageTo?: string;
	newsletterIndexPageTo?: string;
	postIndexPageTo?: string;
	calendarEventIndexPageTo?: string;
};

function CommunicationCenterPageBody({
	view,
	announcementIndexPageTo,
	newsletterIndexPageTo,
	postIndexPageTo,
	calendarEventIndexPageTo,
}: CommunicationCenterPageBodyProps) {
	let announcementFilters: AnnouncementFilterInput | undefined;
	let newsletterFilters: NewsletterFilterInput | undefined;
	let postFilters: PostFilterInput | undefined;
	let calendarEventFilters: CalendarEventFilterInput | undefined;
	if (view === "personal") {
		announcementFilters = { author: [{ isCurrentEQ: true }] };
		newsletterFilters = { author: [{ isCurrentEQ: true }] };
		postFilters = { author: [{ isCurrentEQ: true }] };
		calendarEventFilters = { author: [{ isCurrentEQ: true }] };
	}

	const { data } = useCommunicationCenterStatisticsQuery({
		variables: {
			announcementFilters,
			newsletterFilters,
			postFilters,
			calendarEventFilters,
		},
		fetchPolicy: "cache-and-network",
	});

	const bypassPermissionCheck = React.useCallback(() => view === "all", [view]);

	return (
		<Column justify="spaced-start">
			<PermissionRequired
				requiredPermissions={announcementCreatePermissions}
				checkMethod="or"
				customCheck={bypassPermissionCheck}
				customCheckMethod="or"
			>
				<AnnouncementsSection
					statistics={data?.announcementStatistics}
					filters={announcementFilters}
					indexPageTo={announcementIndexPageTo}
				/>
			</PermissionRequired>

			<PermissionRequired
				requiredPermissions={newsletterCreatePermissions}
				checkMethod="or"
				customCheck={bypassPermissionCheck}
				customCheckMethod="or"
			>
				<NewslettersSection
					statistics={data?.newsletterStatistics}
					filters={newsletterFilters}
					indexPageTo={newsletterIndexPageTo}
				/>
			</PermissionRequired>

			<PermissionRequired
				requiredPermissions={postCreatePermissions}
				checkMethod="or"
				customCheck={bypassPermissionCheck}
				customCheckMethod="or"
			>
				<PostsSection
					view={view}
					statistics={data?.postStatistics}
					filters={postFilters}
					indexPageTo={postIndexPageTo}
				/>
			</PermissionRequired>

			<PermissionRequired
				requiredPermissions={calendarEventCreatePermissions}
				checkMethod="or"
				customCheck={bypassPermissionCheck}
				customCheckMethod="or"
			>
				<CalendarEventsSection
					statistics={data?.calendarEventStatistics}
					filters={calendarEventFilters}
					indexPageTo={calendarEventIndexPageTo}
				/>
			</PermissionRequired>
		</Column>
	);
}

type AnnouncementsSectionProps = {
	statistics?: AnnouncementStatistics;
	filters?: AnnouncementFilterInput;
	indexPageTo?: string;
};

function AnnouncementsSection({
	statistics = defaultAnnouncementStatistics,
	filters,
	indexPageTo,
}: AnnouncementsSectionProps) {
	const { data } = useAnnouncementCommunicationCenterPageListQuery({
		variables: {
			filters,
			order: { field: AnnouncementOrderField.StartTime, direction: OrderDirection.Desc },
			pagination: { first: 3 },
		},
		fetchPolicy: "cache-and-network",
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const maxStatisticValue = Math.max(statistics.views, statistics.reactions);

	let footerContent;
	if (indexPageTo) {
		footerContent = (
			<Row justify="flex-end">
				<InternalLink to={indexPageTo} className="link--primary link--no-text-decoration">
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						View All <Icon.ArrowRight size={16} />
					</Row>
				</InternalLink>
			</Row>
		);
	}

	return (
		<Tile>
			<Tile.Header>
				<Row justify="space-between">
					<Row justify="spaced-start" align="center">
						<AnnouncementModelIcon size={24} />

						<Heading.H2 noMargin>Announcements</Heading.H2>
					</Row>

					<If condition={!!data && data.announcementConnection.edges.length > 0}>
						<Button variant="link" size="small" onClick={openModal}>
							View More
						</Button>
					</If>

					<Modal.If condition={isModalOpen}>
						<ControlledAnnouncementsListModal
							filters={filters}
							onClose={closeModal}
							detailModalVariant="admin"
							footerContent={footerContent}
						/>
					</Modal.If>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Row justify="spaced-start">
					<StatisticsCard>
						<StatisticWithBar
							label="Total"
							value={statistics.count}
							maxValue={statistics.count}
							IconComponent={Icon.Megaphone}
							color={announcementModelColor}
							noGrammaticalNumber
						/>
						<StatisticWithBar
							label="Likes"
							value={statistics.reactions}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ThumbsUp}
							color={statisticColors.reactionsColor}
						/>
						<StatisticWithBar
							label="Views"
							value={statistics.views}
							maxValue={maxStatisticValue}
							IconComponent={Icon.Eye}
							color={statisticColors.viewsColor}
						/>
					</StatisticsCard>

					<Conditional>
						<If condition={!!data && data.announcementConnection.edges.length === 0}>
							<EmptyView
								IconComponent={Icon.Megaphone}
								iconSize={32}
								primaryText="Announcements"
								secondaryText="No active announcements"
							>
								<AnnouncementCreatePermissionRequired>
									<AnnouncementCreateModalController variant="link" size="small">
										Create
									</AnnouncementCreateModalController>
								</AnnouncementCreatePermissionRequired>
							</EmptyView>
						</If>
						<Else>
							<Column justify="spaced-start" verticalSpacing="0.5rem" style={{ flexGrow: 1, minWidth: 0 }}>
								{data?.announcementConnection.edges.map((e) => (
									<AnnouncementAdminCard
										key={e.node.id}
										announcement={e.node}
										className={styles["communication-center__announcement-card"]}
									/>
								))}
							</Column>
						</Else>
					</Conditional>
				</Row>
			</Tile.Body>
		</Tile>
	);
}

type NewslettersSectionProps = {
	statistics?: NewsletterStatistics;
	filters?: NewsletterFilterInput;
	indexPageTo?: string;
};

function NewslettersSection({
	statistics = defaultNewsletterStatistics,
	filters,
	indexPageTo,
}: NewslettersSectionProps) {
	const { data } = useNewsletterDisplayListQuery({
		variables: {
			filters,
			order: { field: NewsletterOrderField.ReleaseAt, direction: OrderDirection.Desc },
			pagination: { first: 3 },
		},
		fetchPolicy: "cache-and-network",
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const maxStatisticValue = Math.max(statistics.views, statistics.reactions, statistics.comments);

	let footerContent;
	if (indexPageTo) {
		footerContent = (
			<Row justify="flex-end">
				<InternalLink to={indexPageTo} className="link--primary link--no-text-decoration">
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						View All <Icon.ArrowRight size={16} />
					</Row>
				</InternalLink>
			</Row>
		);
	}

	return (
		<Tile>
			<Tile.Header>
				<Row justify="space-between">
					<Row justify="spaced-start" align="center">
						<NewsletterModelIcon size={24} />

						<Heading.H2 noMargin>Newsletters</Heading.H2>
					</Row>

					<If condition={!!data && data.newsletterConnection.edges.length > 0}>
						<Button variant="link" size="small" onClick={openModal}>
							View More
						</Button>
					</If>

					<Modal.If condition={isModalOpen}>
						<ControlledNewslettersListModal
							filters={filters}
							onClose={closeModal}
							detailModalVariant="admin"
							footerContent={footerContent}
						/>
					</Modal.If>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Row justify="spaced-start">
					<StatisticsCard>
						<StatisticWithBar
							label="Total"
							value={statistics.count}
							maxValue={statistics.count}
							IconComponent={Icon.NewspaperClipping}
							color={newsletterModelColor}
							noGrammaticalNumber
						/>
						<StatisticWithBar
							label="Likes"
							value={statistics.reactions}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ThumbsUp}
							color={statisticColors.reactionsColor}
						/>
						<StatisticWithBar
							label="Views"
							value={statistics.views}
							maxValue={maxStatisticValue}
							IconComponent={Icon.Eye}
							color={statisticColors.viewsColor}
						/>
						<StatisticWithBar
							label="Comments"
							value={statistics.comments}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ChatCircle}
							color={statisticColors.commentsColor}
						/>
					</StatisticsCard>

					<Conditional>
						<If condition={!!data && data.newsletterConnection.edges.length === 0}>
							<EmptyView
								IconComponent={Icon.NewspaperClipping}
								iconSize={32}
								primaryText="Newsletters"
								secondaryText="No recent newsletters"
							>
								<NewsletterCreatePermissionRequired>
									<NewsletterCreateModalController variant="link" size="small">
										Create
									</NewsletterCreateModalController>
								</NewsletterCreatePermissionRequired>
							</EmptyView>
						</If>
						<Else>
							<Column justify="spaced-start" verticalSpacing="0.5rem" style={{ flexGrow: 1, minWidth: 0 }}>
								{data?.newsletterConnection.edges.map((e) => (
									<NewsletterAdminCard
										key={e.node.id}
										newsletter={e.node}
										className={styles["communication-center__newsletter-card"]}
									/>
								))}
							</Column>
						</Else>
					</Conditional>
				</Row>
			</Tile.Body>
		</Tile>
	);
}

type PostsSectionProps = {
	view: "personal" | "all";
	statistics?: PostStatistics;
	filters?: PostFilterInput;
	indexPageTo?: string;
};

function PostsSection({ view, statistics = defaultPostStatistics, filters, indexPageTo }: PostsSectionProps) {
	const timelineFilter = React.useMemo<TimelineFilterInput>(() => {
		if (view === "personal") {
			return {
				asViewerType: TimelineViewerType.Author,
				objectTypeIn: [TimelineObjectType.Post],
			};
		}
		return {
			objectTypeIn: [TimelineObjectType.Post],
		};
	}, [view]);

	const { data } = useTimelinePostListQuery({
		variables: {
			filters,
			order: { field: PostOrderField.CreatedAt, direction: OrderDirection.Desc },
			pagination: { first: 3 },
		},
		fetchPolicy: "cache-and-network",
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const maxStatisticValue = Math.max(statistics.views, statistics.reactions, statistics.comments);

	return (
		<Tile>
			<Tile.Header>
				<Row justify="space-between">
					<Row justify="spaced-start" align="center">
						<PostModelIcon size={24} />

						<Heading.H2 noMargin>Posts</Heading.H2>
					</Row>

					<If condition={!!data && data.postConnection.edges.length > 0}>
						<Button variant="link" size="small" onClick={openModal}>
							View More
						</Button>
					</If>

					<Modal.If condition={isModalOpen}>
						<Modal onClose={closeModal} style={{ width: "50rem", height: "50rem" }}>
							<Modal.Header>
								<Heading.H2 noMargin>Posts</Heading.H2>
							</Modal.Header>
							<Modal.Body>
								<Timeline filter={timelineFilter} pageSize={10} modalVariant="admin" style={{ height: "100%" }} />
							</Modal.Body>
							<If condition={!!indexPageTo}>
								<Modal.Footer>
									<Row justify="flex-end">
										<InternalLink to={indexPageTo ?? "#"} className="link--primary link--no-text-decoration">
											<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
												View All <Icon.ArrowRight size={16} />
											</Row>
										</InternalLink>
									</Row>
								</Modal.Footer>
							</If>
						</Modal>
					</Modal.If>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Row justify="spaced-start">
					<StatisticsCard>
						<StatisticWithBar
							label="Total"
							value={statistics.count}
							maxValue={statistics.count}
							IconComponent={Icon.ImagesSquare}
							color={postModelColor}
							noGrammaticalNumber
						/>
						<StatisticWithBar
							label="Likes"
							value={statistics.reactions}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ThumbsUp}
							color={statisticColors.reactionsColor}
						/>
						<StatisticWithBar
							label="Views"
							value={statistics.views}
							maxValue={maxStatisticValue}
							IconComponent={Icon.Eye}
							color={statisticColors.viewsColor}
						/>
						<StatisticWithBar
							label="Comments"
							value={statistics.comments}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ChatCircle}
							color={statisticColors.commentsColor}
						/>
					</StatisticsCard>

					<Conditional>
						<If condition={!!data && data.postConnection.edges.length === 0}>
							<EmptyView
								IconComponent={Icon.ImagesSquare}
								iconSize={32}
								primaryText="Posts"
								secondaryText="No recent posts"
							>
								<PostCreatePermissionRequired>
									<PostCreateModalController variant="link" size="small">
										Create
									</PostCreateModalController>
								</PostCreatePermissionRequired>
							</EmptyView>
						</If>
						<Else>
							<Column justify="spaced-start" verticalSpacing="0.5rem" style={{ flexGrow: 1, minWidth: 0 }}>
								{data?.postConnection.edges.map((e) => (
									<PostAdminCard key={e.node.id} post={e.node} className={styles["communication-center__post-card"]} />
								))}
							</Column>
						</Else>
					</Conditional>
				</Row>
			</Tile.Body>
		</Tile>
	);
}

type CalendarEventsSectionProps = {
	statistics?: CalendarEventStatistics;
	filters?: CalendarEventFilterInput;
	indexPageTo?: string;
};

function CalendarEventsSection({
	statistics = defaultCalendarEventStatistics,
	filters,
	indexPageTo,
}: CalendarEventsSectionProps) {
	const timedFilters = {
		startTimeIsNull: false,
		startTimeGTE: dateTrunc(addTimeToDate(new Date(), [2, "hour"]), "hour").toISOString(),
	};
	const { data: timedData } = useCalendarEventCalendarDetailedListQuery({
		variables: {
			filters: filters ? mergeFilterInputs(filters, timedFilters) : timedFilters,
			order: { field: CalendarEventOrderField.StartTime, direction: OrderDirection.Asc },
			pagination: { first: 3 },
		},
		fetchPolicy: "cache-and-network",
	});
	const allDayFitlers = { startDateIsNull: false, startDateGT: toLocalDateString(new Date()) };
	const { data: allDayData } = useCalendarEventCalendarDetailedListQuery({
		variables: {
			filters: filters ? mergeFilterInputs(filters, allDayFitlers) : allDayFitlers,
			order: { field: CalendarEventOrderField.StartDate, direction: OrderDirection.Asc },
			pagination: { first: 3 },
		},
		fetchPolicy: "cache-and-network",
	});

	const sortedEventEdges = sort(
		[...(timedData?.calendarEventConnection.edges ?? []), ...(allDayData?.calendarEventConnection.edges ?? [])],
		CalendarEventUtilities.compareCalendarEventEdgesAsc(),
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const maxStatisticValue = Math.max(
		statistics.responsesAttending,
		statistics.responsesNotAttending,
		statistics.responsesMaybeAttending,
	);

	return (
		<Tile>
			<Tile.Header>
				<Row justify="space-between">
					<Row justify="spaced-start" align="center">
						<CalendarEventModelIcon size={24} />

						<Heading.H2 noMargin>Events</Heading.H2>
					</Row>

					<Button variant="link" size="small" onClick={openModal}>
						View Calendar
					</Button>

					<Modal.If condition={isModalOpen}>
						<Modal size="large" onClose={closeModal}>
							<Modal.Body style={{ height: "100%", minHeight: 0 }}>
								<ViewerBigCalendar detailModalVariant="admin" filters={filters} style={{ height: "100%" }} />
							</Modal.Body>
							<If condition={!!indexPageTo}>
								<Modal.Footer>
									<Row justify="flex-end">
										<InternalLink to={indexPageTo ?? "#"} className="link--primary link--no-text-decoration">
											<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
												View All <Icon.ArrowRight size={16} />
											</Row>
										</InternalLink>
									</Row>
								</Modal.Footer>
							</If>
						</Modal>
					</Modal.If>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Row justify="spaced-start">
					<StatisticsCard>
						<StatisticWithBar
							label="Total"
							value={statistics.count}
							maxValue={statistics.count}
							IconComponent={Icon.CalendarBlank}
							color={calendarEventModelColor}
							noGrammaticalNumber
						/>
						<StatisticWithBar
							label="Going"
							value={statistics.responsesAttending}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ThumbsUp}
							color={statisticColors.responseGoingColor}
							noGrammaticalNumber
						/>
						<StatisticWithBar
							label="Not Going"
							value={statistics.responsesNotAttending}
							maxValue={maxStatisticValue}
							IconComponent={Icon.ThumbsDown}
							color={statisticColors.responseNotGoingColor}
							noGrammaticalNumber
						/>
						<StatisticWithBar
							label="Maybe"
							value={statistics.responsesMaybeAttending}
							maxValue={maxStatisticValue}
							IconComponent={Icon.Question}
							color={statisticColors.responseMaybeColor}
							noGrammaticalNumber
						/>
					</StatisticsCard>

					<Conditional>
						<If condition={(!!allDayData || !!timedData) && sortedEventEdges.length === 0}>
							<EmptyView
								IconComponent={Icon.CalendarBlank}
								iconSize={32}
								primaryText="Events"
								secondaryText="No upcoming events"
							>
								<CalendarEventCreatePermissionRequired>
									<CalendarEventCreateModalController variant="link" size="small">
										Create
									</CalendarEventCreateModalController>
								</CalendarEventCreatePermissionRequired>
							</EmptyView>
						</If>
						<Else>
							<Column justify="spaced-start" verticalSpacing="0.5rem" style={{ flexGrow: 1, minWidth: 0 }}>
								{sortedEventEdges.slice(0, 3).map((e) => (
									<CalendarEventAdminCard
										key={e.node.id}
										calendarEvent={e.node}
										className={styles["communication-center__calendar-event-card"]}
									/>
								))}
							</Column>
						</Else>
					</Conditional>
				</Row>
			</Tile.Body>
		</Tile>
	);
}

type StatisticsCardProps = Partial<ClassNameProps & StyleProps & ChildrenProps>;

function StatisticsCard({ className, style, children }: StatisticsCardProps) {
	return (
		<Section className={makeClassName(styles["statistics-card"], className)} style={style}>
			<Section.Header>
				<Heading.H3 noMargin style={{ fontSize: "1rem" }}>
					Statistics
				</Heading.H3>
			</Section.Header>
			<Section.Body>
				<Column justify="center" style={{ height: "100%" }}>
					<Grid tracks={2} gap="0.75rem 1rem">
						{children}
					</Grid>
				</Column>
			</Section.Body>
		</Section>
	);
}
